import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { IOrganization } from '@pec/aion-ui-core/interfaces/organization';
import { getComplianceLocation } from '@pec/aion-ui-core/helpers/complianceRegion';
import { enqueueRequestErrorSnackbar } from '@pec/aion-ui-core/actions/enqueueSnackbar';
import { TextField, InputAdornment, CircularProgress, Popper, Paper, Link, Typography, Box } from '@material-ui/core';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { fade } from '@material-ui/core/styles/colorManipulator';
import SearchIcon from '@material-ui/icons/Search';
import axios from 'axios';
import Cookies from 'js-cookie';
import { DeepReadonly } from 'utility-types';
import { debounce, throttle, uniqBy } from 'lodash';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    search: {
      width: 570
    },
    searchInput: {
      height: 35,
      color: theme.palette.common.white,
      border: 'none',
      transition: '0.3s',
      backgroundColor: fade(theme.palette.common.white, 0.15),
      '& fieldset': {
        border: 'none'
      },
      '&:hover': {
        backgroundColor: fade(theme.palette.common.white, 0.25)
      }
    },
    results: {
      width: 570,
      maxHeight: 350,
      overflowY: 'auto',
      margin: 0,
      padding: 0
    },
    listItem: {
      listStyleType: 'none',
      transition: '0.25s',
      padding: '8px 16px',
      '& a': { color: theme.palette.text.primary },
      '&:hover': { background: 'rgba(0, 110, 150, 0.078)' },
      '& svg': {
        fontSize: 16,
        verticalAlign: 'text-top',
        marginLeft: 4
      }
    },
    sectionHeader: {
      listStyleType: 'none',
      padding: '8px 16px 4px',
      color: theme.palette.text.secondary,
      fontSize: '0.7rem',
      fontWeight: 600,
      letterSpacing: '0.08em',
      textTransform: 'uppercase' as const
    },
    contractorName: {
      fontWeight: 600,
      fontSize: '0.9rem'
    },
    contractorAddress: {
      color: theme.palette.text.secondary
    },
    complianceSquare: {
      width: 12,
      height: 12,
      borderRadius: 2,
      marginRight: 6,
      flexShrink: 0
    },
    statusText: {
      color: theme.palette.text.secondary
    }
  })
);

// Fields returned by HeaderSearchResultDto (org-search micro v3.04 headerSearch)
interface IContractor {
  id: string;
  name: string;
  ssqId: number | null;
  city: string | null;
  state: string | null;
  connectionStatus: number;
  rank: number | null;
  rankDescription: string | null;
  color: string | null;
  accreditationTypeName: string | null;
  isChas: boolean;
}

const MIN_SEARCH_LENGTH = 3;
const ITEM_PER_PAGE = 10;

type Props = {
  organization?: DeepReadonly<IOrganization> | null;
};

const ContractorSearch: React.FC<Props> = ({ organization }) => {
  const [search, setSearch] = useState<string>('');
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [results, setResults] = useState<IContractor[]>([]);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [page, setPage] = useState<number>(0);
  const resultsRef = useRef(results);
  const pageRef = useRef(page);
  useEffect(() => {
    resultsRef.current = results;
  }, [results]);
  useEffect(() => {
    pageRef.current = page;
  }, [page]);
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();

  useEffect(() => {
    page && fetchResults(search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleSearch = useCallback(
    debounce((search: string) => {
      search.length >= MIN_SEARCH_LENGTH && fetchResults(search);
    }, 300),
    []
  );

  const handleChangeSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSearch(value);
    setPage(0);
    if (value.length < MIN_SEARCH_LENGTH) {
      setResults([]);
    } else {
      handleSearch(value);
    }
  };

  const handleScroll = throttle(({ scrollTop, clientHeight, scrollHeight }) => {
    if (scrollTop >= scrollHeight - clientHeight) {
      if (resultsRef.current.length === (pageRef.current + 1) * ITEM_PER_PAGE) {
        setPage(pageRef.current + 1);
      }
    }
  }, 250);

  const fetchResults = async (search: string) => {
    try {
      setIsFetching(true);
      !page && setResults([]);
      const complianceLocation = organization?.features ? getComplianceLocation(organization.features) : undefined;
      const { data } = await axios.get<{ value: IContractor[]; count: number }>(
        `/api/v3.04/organizations/contractorSearch`,
        {
          params: {
            keyword: search,
            top: ITEM_PER_PAGE,
            skip: page * ITEM_PER_PAGE,
            ...(complianceLocation && { complianceLocation })
          }
        }
      );
      page ? setResults(prev => [...prev, ...data.value]) : setResults(data.value);
    } catch (error) {
      dispatch(enqueueRequestErrorSnackbar());
      throw error;
    } finally {
      setIsFetching(false);
    }
  };

  return (
    <ClickAwayListener onClickAway={() => setAnchor(null)}>
      <Box>
        <TextField
          id="search-contractor"
          className={classes.search}
          placeholder={t('components.contractorSearch.searchForAContractor', 'Search for a contractor')}
          variant="outlined"
          fullWidth
          value={search}
          onChange={handleChangeSearch}
          onFocus={event => setAnchor(event.currentTarget)}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            className: classes.searchInput
          }}
        />
        <Popper
          open={Boolean(search.length >= MIN_SEARCH_LENGTH) && Boolean(anchor)}
          anchorEl={anchor}
          placement="bottom-end"
          disablePortal={true}
        >
          <Paper>
            <ul
              className={classes.results}
              onScroll={(event: React.SyntheticEvent) => handleScroll(event.currentTarget)}
            >
              {(() => {
                const uniqueResults = uniqBy(results, 'id');
                const myContractors = uniqueResults.filter(c => c.connectionStatus === 0);
                const otherMatches = uniqueResults.filter(c => c.connectionStatus === 1 || c.connectionStatus === 2);

                const renderContractor = (contractor: IContractor) => {
                  const isConnected = contractor.connectionStatus !== 2;
                  const primaryAddress = [contractor.city, contractor.state].filter(Boolean).join(', ');
                  const squareColor = isConnected && contractor.color ? contractor.color : null;
                  const statusLabel = isConnected
                    ? contractor.rankDescription || null
                    : t('components.contractorSearch.notConnected', 'Not Connected');
                  // Connected non-CHAS → Dashboard tab (tabId=0); CHAS and not-connected → Company Info tab (tabId=7)
                  const profileUrl = `/SSQV4/SSQV5/Contractor/Index?id=${contractor.id}`;
                  const tabId = isConnected && !contractor.isChas ? '0' : '7';

                  const handleLegacyClick = () => {
                    if (organization?.id) {
                      Cookies.set('AionCurrentOrganizationId', organization.id, { sameSite: 'lax' });
                    }
                    localStorage.setItem('tabId', tabId);
                  };

                  return (
                    <li key={contractor.id} className={classes.listItem}>
                      <Link href={profileUrl} onClick={handleLegacyClick}>
                        <Typography className={classes.contractorName}>
                          {contractor.name}
                          {contractor.ssqId ? ` (${contractor.ssqId})` : ''}
                        </Typography>
                        {primaryAddress && (
                          <Typography variant="body2" className={classes.contractorAddress}>
                            {primaryAddress}
                          </Typography>
                        )}
                        <Box display="flex" alignItems="center">
                          {squareColor && (
                            <Box className={classes.complianceSquare} style={{ backgroundColor: squareColor }} />
                          )}
                          {statusLabel && (
                            <Typography variant="body2" className={classes.statusText}>
                              {statusLabel}
                            </Typography>
                          )}
                        </Box>
                      </Link>
                    </li>
                  );
                };

                return (
                  <>
                    {myContractors.length > 0 && (
                      <>
                        <li className={classes.sectionHeader}>
                          {t('components.contractorSearch.myContractors', 'My Contractor')}
                        </li>
                        {myContractors.map(renderContractor)}
                      </>
                    )}
                    {otherMatches.length > 0 && (
                      <>
                        <li className={classes.sectionHeader}>
                          {t('components.contractorSearch.otherPossibleMatches', 'Possible matches')}
                        </li>
                        {otherMatches.map(renderContractor)}
                      </>
                    )}
                  </>
                );
              })()}
              {isFetching && (
                <li className={classes.listItem} style={{ textAlign: 'center' }}>
                  <CircularProgress size={18} />
                </li>
              )}
              {!isFetching && !results.length && (
                <li className={classes.listItem} style={{ textAlign: 'center' }}>
                  <Typography variant="body2">
                    {t('components.contractorSearch.noResultsFound', 'No results found')}
                  </Typography>
                </li>
              )}
            </ul>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default ContractorSearch;
